import React from 'react'
import { useTranslation } from 'react-i18next'
import { Stack, Box, Typography, Tooltip, Skeleton } from '@mui/material'
import { useNavigate } from 'react-router-dom'

import useSsidNetworks from '../use-ssid-networks'
import { trackEvent } from 'analytics'

const NetworkCard = ({ title, color, items, loading }) => {
  const { t } = useTranslation()
  
  if (loading) {
    return (
      <Box
        sx={{
          flex: 1,
          border: '1px solid #EFEFEF',
          borderRadius: '12px',
          p: 3,
          minHeight: '130px',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Stack direction="row" gap={1} alignItems="center" justifyContent="center">
          <Skeleton variant="circular" width={8} height={8} />
          <Skeleton variant="text" width={80} height={20} />
        </Stack>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Stack gap={0.5} alignItems="center" sx={{ width: '100%' }}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="text" width="70%" height={16} />
            ))}
          </Stack>
        </Box>
      </Box>
    )
  }
  
  return (
    <Box
      sx={{
        flex: 1,
        border: '1px solid #EFEFEF',
        borderRadius: '12px',
        p: 3,
        minHeight: '130px',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Stack direction="row" gap={1} alignItems="center" justifyContent="center">
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: color
          }}
        />
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#464646' }}>
          {title}
        </Typography>
      </Stack>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 2
        }}
      >
        {items.length === 0 ? (
          <Typography sx={{ fontSize: 12, fontWeight: 400, color: '#ABABAB', textAlign: 'center' }}>
            {t('event_ssids.pre_configured.no_networks')}
          </Typography>
        ) : (
          <Stack gap={0.5} alignItems="center">
            {items.map((item, idx) => (
              <Typography key={idx} sx={{ fontSize: 12, fontWeight: 400, color: '#656565', textAlign: 'center' }}>
                {item.label}
              </Typography>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  )
}

const StatsCard = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleClick = () => {
    trackEvent('wifi_management_statistics_opened')
    navigate('/stats/usage')
  }

  return (
    <Box
      onClick={handleClick}
      sx={{
        flex: 1,
        border: '1px solid #EFEFEF',
        borderRadius: '12px',
        p: 2,
        minHeight: '130px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F7F7F7',
        gap: 1.5,
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        '&:hover': {
          backgroundColor: '#EFEFEF'
        }
      }}
    >
      <i className="fa-solid fa-square-poll-vertical" style={{ fontSize: 24, color: '#464646' }} />
      <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#464646', textAlign: 'center' }}>
        {t('event_ssids.pre_configured.view_statistics')}
      </Typography>
    </Box>
  )
}

const PreConfiguredNetworks: React.FC = () => {
  const { t } = useTranslation()
  const { loading, networksByType } = useSsidNetworks()

  return (
    <Stack gap={2}>
      <Stack direction="row" gap={1} alignItems="center">
        <i className="fa-regular fa-signal-stream-slash" style={{ fontSize: 14, color: '#2570EB' }} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#464646' }}>
          {t('event_ssids.pre_configured.title')}
        </Typography>
        <Tooltip title={t('event_ssids.pre_configured.tooltip')} arrow>
          <i className="fa-solid fa-circle-info" style={{ fontSize: 12, color: '#656565', cursor: 'pointer' }} />
        </Tooltip>
      </Stack>

      <Stack direction="row" gap={2} alignItems="stretch">
        <NetworkCard
          title="Guest"
          color="#EC7A74"
          items={networksByType.guest}
          loading={loading}
        />
        <NetworkCard
          title="Privé"
          color="#4F8AA4"
          items={networksByType.private}
          loading={loading}
        />
        <NetworkCard
          title="Métier"
          color="#A5D9CB"
          items={networksByType.business}
          loading={loading}
        />
        <StatsCard />
      </Stack>
    </Stack>
  )
}

export default PreConfiguredNetworks